import { MutableRefObject, useCallback, useEffect, useState } from "react";
import SignaturePad from "signature_pad";
import { useSignatureController } from "@/components/signiture/use-signature-controller";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useGetSignatureByProviderID } from "@/queries/signature/use-get-signature-by-providerID";
import { getErrorSnackbar, getXtenantId } from "@/utils/utils";
import { ADDENDUM_LABELS } from "../visit-detail-constant";
import { drawSignatureFitted, getSavedSignature } from "../visit-detail-utils";

export interface AddendumSignature {
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  signPad?: SignaturePad;
  savedSignature?: string;
  hasSignature: boolean;
  signatureError?: string;
  markSigned: () => void;
  importSignature: () => Promise<void>;
  clearSignature: () => void;
  resetSignature: () => void;
}

export const useAddendumSignature = (isActive: boolean): AddendumSignature => {
  const showSnackbar = useSnackbar();
  const [currentUser] = useCurrentUser();
  const [hasSignature, setHasSignature] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const { canvasRef, signPad } = useSignatureController(isActive);
  const { data: signatureData } = useGetSignatureByProviderID({
    providerUuid: currentUser?.uuid ?? "",
    xTENANTID: getXtenantId(),
  });
  const savedSignature = getSavedSignature(signatureData?.data);

  useEffect(() => {
    if (!signPad) return;
    const onEndStroke = () => setHasSignature(!signPad.isEmpty());
    signPad.addEventListener("endStroke", onEndStroke);
    return () => signPad.removeEventListener("endStroke", onEndStroke);
  }, [signPad]);

  const markSigned = useCallback(() => {
    setHasSignature(true);
    setIsTouched(true);
  }, []);

  const importSignature = useCallback(async () => {
    if (!signPad || !canvasRef.current || !savedSignature) return;
    try {
      await drawSignatureFitted(signPad, canvasRef.current, savedSignature);
      markSigned();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [signPad, canvasRef, savedSignature, markSigned, showSnackbar]);

  const clearSignature = useCallback(() => {
    signPad?.clear();
    setHasSignature(false);
    setIsTouched(true);
  }, [signPad]);

  const resetSignature = useCallback(() => {
    setHasSignature(false);
    setIsTouched(false);
  }, []);

  return {
    canvasRef,
    signPad,
    savedSignature,
    hasSignature,
    signatureError:
      isTouched && !hasSignature
        ? ADDENDUM_LABELS.signatureRequired
        : undefined,
    markSigned,
    importSignature,
    clearSignature,
    resetSignature,
  };
};
